/**
 * Vertical × Discipline matrix data for SEO landing pages.
 *
 * 9 industry verticals × 4 security disciplines = 36 combination pages.
 * Each combination has its own URL like `/hotel-access-control-design`
 * targeting buyer-stage queries (security integrators specifying systems
 * for their target vertical).
 *
 * Content strategy: rather than hand-writing 36 unique pages, write 9
 * vertical narratives + 4 discipline narratives once, then combine them
 * in the template. 13 narrative blocks → 36 pages. Each combo page is
 * still semantically unique to Google because the (vertical, discipline)
 * pairing produces distinct title, h1, body intro, FAQ, and use cases.
 *
 * Disciplines reuse the 4 pillar pages already shipped:
 *  - access-control-design
 *  - intrusion-detection-design
 *  - network-planning-for-security
 *  - security-compliance
 *
 * Verticals 1-9 are NEW. Existing /use-cases/* (warehouse, retail, parking,
 * school, construction, industrial) are CCTV-only and stay untouched —
 * future sprint can add discipline cross-links to those.
 */

export type DisciplineKey =
  | 'access-control'
  | 'intrusion-detection'
  | 'network-planning'
  | 'security-compliance';

export interface VerticalFacts {
  slug: string;
  name: string;
  /** One-line industry context for the hero subtitle */
  oneLine: string;
  /** 3-4 sentence narrative for the intro paragraph */
  intro: string;
  /** Industry-specific pain points (3-5 items) */
  painPoints: string[];
  /** Compliance / regulatory frameworks that apply */
  compliance: string[];
  /** Typical camera count range for this vertical (rough sanity-check) */
  typicalCameras: [number, number];
  /** Recommended brand tier (matches BRAND_FACTS.tier) */
  recommendedBrandTier: 'consumer' | 'prosumer' | 'professional' | 'enterprise';
  /** Brand slugs (from BRAND_FACTS) that fit this vertical best */
  topBrands: string[];
}

export interface DisciplineFacts {
  slug: DisciplineKey;
  name: string;
  /** Used as second half of the page H1 (after vertical name) */
  shortName: string;
  /** Generic discipline explanation (5-6 sentences) */
  about: string;
  /** Cross-link target — already-shipped pillar page */
  pillarUrl: string;
  /** Capabilities specific to this discipline (5-7 items) */
  capabilities: string[];
}

// ── 9 new industry verticals ──────────────────────────────────────────────
export const VERTICALS: Record<string, VerticalFacts> = {
  hotel: {
    slug: 'hotel',
    name: 'Hotel',
    oneLine: 'Guest-facing properties balance security with hospitality — invisible coverage in lobbies, unobtrusive sensors in corridors, no cameras in private spaces.',
    intro: 'Hotel security design walks a fine line between protecting guests and staff without making the property feel like an airport. Cameras need to cover entrances, lobbies, corridors, parking, and back-of-house — but absolutely not guest rooms or bathrooms. Access control overlays the guest credential system (RFID keycards), and intrusion detection focuses on staff-only zones (back office, key storage, F&B inventory).',
    painPoints: [
      'Guest privacy laws (GDPR Art. 6, US state laws) restrict camera placement in private spaces',
      'Lobby coverage must blend with the design — visible cameras hurt the brand at luxury tier',
      'Staff/guest access zones change daily (housekeeping floors, banquet hall events)',
      'After-hours coverage of pool, gym, business center where guest access is limited',
      'Lost-property liability requires clear chain-of-custody footage from corridors and storage',
    ],
    compliance: ['GDPR (EU)', 'PCI-DSS (POS systems)', 'ADA / accessibility', 'Fire alarm interoperability'],
    typicalCameras: [40, 200],
    recommendedBrandTier: 'professional',
    topBrands: ['hanwha', 'axis', 'bosch'],
  },
  hospital: {
    slug: 'hospital',
    name: 'Hospital',
    oneLine: 'Healthcare facilities demand patient privacy, infection control, 24/7 staff safety monitoring, and integration with emergency response.',
    intro: 'Hospital security is uniquely complex: HIPAA / patient confidentiality blocks cameras in treatment rooms, while emergency department staff need duress buttons and panic alarms. Pharmacy and drug storage require dual-credential access control with full audit trails. Network segmentation isolates medical device VLANs from CCTV traffic — a single breach can shut down anaesthesia monitors.',
    painPoints: [
      'HIPAA / patient privacy bans cameras in exam, treatment, and patient rooms',
      'Maternity / paediatrics need anti-elopement tracking (infant tagging, exit door monitoring)',
      'Pharmacy + drug storage require dual-authentication access + 7-year audit logs',
      'ED / triage needs duress alarms wired to security dispatch + body-worn cameras for staff',
      'Behavioural health units have strict ligature-resistant camera mount requirements',
    ],
    compliance: ['HIPAA (US)', 'GDPR Article 9 (EU sensitive data)', 'Joint Commission', 'DEA Schedule II/III storage'],
    typicalCameras: [80, 500],
    recommendedBrandTier: 'enterprise',
    topBrands: ['axis', 'hanwha', 'bosch'],
  },
  bank: {
    slug: 'bank',
    name: 'Bank',
    oneLine: 'Financial institutions follow strict regulatory specifications: facial-recognition-grade resolution at teller positions, tamper-proof DVR, 7-year retention, perimeter intrusion alarms.',
    intro: 'Bank security is dictated by regulation more than design choice. Federal Reserve guidelines (US), Basel III banking supervision (EU), and PCI-DSS for card data all impose hard requirements: minimum pixel density at teller positions, retention windows of 30-90 days minimum (some jurisdictions 7 years for fraud cases), redundant alarm reporting, and 24/7 monitored intrusion detection. Branches need vestibule mantraps with bullet-resistant interlocks.',
    painPoints: [
      'Facial-recognition-grade resolution at teller (≥250 px/m per EN 62676-4)',
      'ATM camera fields-of-view captured both transaction + face-of-user simultaneously',
      'Vault + safe deposit rooms require dual-control access (two cards + PIN)',
      'Branch perimeter intrusion sensors armed automatically at close-of-business',
      'Reporting to monitoring station + police is regulated — must be redundant (cellular + IP)',
    ],
    compliance: ['PCI-DSS', 'Federal Reserve (US)', 'Basel III (EU)', 'FATF audit trails', 'EN 50131 Grade 3'],
    typicalCameras: [25, 80],
    recommendedBrandTier: 'enterprise',
    topBrands: ['bosch', 'axis', 'hanwha'],
  },
  'gas-station': {
    slug: 'gas-station',
    name: 'Gas Station',
    oneLine: 'Forecourt cameras need ANPR for drive-offs, all-weather IK10 housings, and integration with POS for shrinkage tracking.',
    intro: 'Gas station security combines exterior forecourt coverage (license plate capture, drive-off detection) with interior convenience-store loss prevention. Pump-area cameras need IK10 vandal resistance and IP67 weather sealing. ANPR cameras at entry/exit drive POS integration: if a vehicle leaves without paying, the system flags the plate. After-hours unmanned operation requires intrusion detection on the kiosk and remote-monitored alarm response.',
    painPoints: [
      'ANPR cameras must capture plates day/night at 60+ km/h vehicle speed',
      'Pump-area cameras exposed to vandalism, vapours, washdown (IP67 + IK10)',
      'POS integration logs every transaction with timestamped video frame',
      'Convenience-store shrinkage at register, cooler, baked-goods aisle',
      'After-hours intrusion alarms + dual-tech sensors for unmanned 24h sites',
    ],
    compliance: ['PCI-DSS (POS)', 'GDPR / state privacy on ANPR', 'Environmental Protection (vapour-rated housings)'],
    typicalCameras: [8, 24],
    recommendedBrandTier: 'professional',
    topBrands: ['hikvision', 'dahua', 'hanwha'],
  },
  office: {
    slug: 'office',
    name: 'Office Building',
    oneLine: 'Multi-tenant offices need badge-based access at the lobby + each floor, after-hours intrusion, and visitor management.',
    intro: 'Office security designs differ wildly between single-tenant and multi-tenant buildings. Single-tenant: HQ-level access control (every door badged, executive suites segmented), CCTV at building entrances + parking + datacenter, intrusion arming linked to alarm-to-vacancy schedule. Multi-tenant: lobby reception + elevator destination dispatch, per-tenant access zones, shared CCTV for common areas with tenant-specific recording vaults.',
    painPoints: [
      'Per-tenant access zones (each floor / suite) without cross-tenant data leakage',
      'Visitor management integrated with badge issuance (temporary credentials, time-limited)',
      'After-hours arming: each tenant arms their suite separately on different schedules',
      'Executive suites segmented (executive-only access, separate video vault)',
      'Cycle parking + EV charging zones need cameras + access control',
    ],
    compliance: ['GDPR (EU)', 'CCPA (California)', 'SOX (US public companies)', 'EN 50131 Grade 2-3'],
    typicalCameras: [30, 150],
    recommendedBrandTier: 'professional',
    topBrands: ['hanwha', 'axis', 'ubiquiti'],
  },
  'multi-tenant-residential': {
    slug: 'multi-tenant-residential',
    name: 'Apartment Building',
    oneLine: 'Apartment buildings need lobby + corridor cameras, intercom-integrated access, garage / mailroom intrusion sensors.',
    intro: 'Multi-tenant residential security is about common areas, not private apartments. Lobby cameras + video intercom let residents see visitors; corridor cameras cover hallways and stairwells; garage cameras protect parked vehicles + EV chargers; mailroom + parcel-locker cameras prevent theft. Access control uses fobs or BLE phone credentials at entry, garage gate, and amenity rooms (gym, pool, business center). Strict no-cameras in private apartments.',
    painPoints: [
      'Lobby + corridor + stairwell coverage without intruding on apartment privacy',
      'Video intercom integrates with door access (let-in by phone or apartment unit)',
      'Garage + EV charger cameras with license plate capture for unauthorized entry',
      'Mailroom + parcel-locker theft is a fast-growing problem (Amazon-era)',
      'Amenity rooms (gym, pool, party room) need scheduled access + duress button',
    ],
    compliance: ['GDPR (EU)', 'state landlord-tenant laws', 'fire safety (cameras must not block egress)'],
    typicalCameras: [20, 100],
    recommendedBrandTier: 'prosumer',
    topBrands: ['ubiquiti', 'hanwha', 'reolink'],
  },
  factory: {
    slug: 'factory',
    name: 'Factory',
    oneLine: 'Industrial sites need cameras with IP67 + IK10 ratings for harsh environments, integration with SCADA, and explosion-proof housings in hazardous zones.',
    intro: 'Factory security combines perimeter intrusion (fence-line beam barriers, ground sensors) with interior process monitoring (line cameras for quality + safety, control room access). Harsh environments demand IP67 + IK10 cameras with explosion-proof (ATEX/IECEx) housings in solvent or dust zones. SCADA / OT network is air-gapped from CCTV LAN. Worker-safety cameras at high-risk machinery (presses, robotic cells) trigger e-stops on intrusion.',
    painPoints: [
      'Perimeter intrusion across hectares of fenced site (beam barriers, fence detection)',
      'Hazardous zones (paint booth, solvent storage) need ATEX/IECEx-rated cameras',
      'OT/SCADA network must be air-gapped from CCTV — VLAN isolation + firewall ACLs',
      'Worker safety: man-down detection, machine-guarding cameras with e-stop integration',
      'After-hours unmanned operation: full perimeter armed + interior motion + thermal',
    ],
    compliance: ['ATEX / IECEx (EU hazardous zones)', 'OSHA (US worker safety)', 'IEC 61508 (functional safety)'],
    typicalCameras: [60, 300],
    recommendedBrandTier: 'professional',
    topBrands: ['axis', 'bosch', 'flir'],
  },
  'data-center': {
    slug: 'data-center',
    name: 'Data Center',
    oneLine: 'Tier III/IV data centers require dual-control access, biometrics at the cage, 100% CCTV coverage of every aisle, and intrusion sensors on every hall.',
    intro: 'Data center security is among the strictest in the security industry. Tier III and IV facilities require: dual-control access at every entry (two credentials, two persons), biometric verification at cage / suite level, 100% CCTV coverage of every aisle / row / cabinet rear, intrusion sensors on every hall and ceiling void, environmental monitoring (smoke / heat / leak), and network segmentation that isolates CCTV LAN from customer VLANs. Retention: 90+ days, often 1 year for compliance audits.',
    painPoints: [
      'Mantrap + dual-control entry (anti-tailgating, two-person rule)',
      'Cage-level biometrics (fingerprint or iris) on customer suites',
      '100% aisle coverage at row-end + ceiling void intrusion',
      'OT/IT segmentation: CCTV LAN must NOT see customer data plane',
      'Tier III/IV uptime requires redundant NVR, dual PoE switches, A+B power feeds',
    ],
    compliance: ['SOC 2', 'ISO 27001', 'PCI-DSS', 'HIPAA (healthcare hosting)', 'Uptime Institute Tier III/IV'],
    typicalCameras: [50, 300],
    recommendedBrandTier: 'enterprise',
    topBrands: ['axis', 'bosch', 'hanwha'],
  },
  transportation: {
    slug: 'transportation',
    name: 'Transit Hub',
    oneLine: 'Transit hubs need 24/7 platform / concourse coverage, ANPR at vehicle entries, anti-terror intrusion at perimeter, and integration with passenger-information systems.',
    intro: 'Transit-hub security (airports, rail stations, bus terminals, ports) requires public-space CCTV at the highest density — platforms, concourses, ticketing, baggage areas — combined with restricted-zone access control (airside, control tower, signalling room). ANPR scans every vehicle entering the parking structure or service road. Anti-terror perimeter intrusion uses fence-line beam barriers + ground sensors. PA / passenger-information systems integrate with security (lockdown announcements, evacuation routing).',
    painPoints: [
      'Public-area density: platform / concourse / ticket-hall at ≤2 m blind-spot grid',
      'Airside / restricted-zone access with biometric secondary verification',
      'ANPR at all vehicle entries (drive-up, taxi rank, staff lot)',
      'Anti-terror perimeter: beam barriers + ground sensors + IR illumination',
      'PA integration for lockdown / evacuation announcements triggered by security',
    ],
    compliance: ['TSA (US aviation)', 'EU 2015/1998 (aviation)', 'PIDS (perimeter intrusion standards)', 'GDPR'],
    typicalCameras: [200, 2000],
    recommendedBrandTier: 'enterprise',
    topBrands: ['axis', 'bosch', 'flir', 'hanwha'],
  },
};

// ── 4 disciplines (cross-link to existing pillar pages) ────────────────
export const DISCIPLINES: Record<DisciplineKey, DisciplineFacts> = {
  'access-control': {
    slug: 'access-control',
    name: 'Access Control',
    shortName: 'access control design',
    about: 'Access control planning covers credential technology (RFID cards, BLE phones, biometrics), door hardware (electric strikes, maglocks, mortise locks), control panels (Mercury, HID, AXIS, Suprema), and the network architecture that ties them together. Multi-credential verification (card + PIN, card + biometric) raises Grade 3 compliance; dual-control access (two badged users) protects high-value zones. Schedules let access automatically time-restrict to shift hours, holidays, or visitor windows.',
    pillarUrl: '/access-control-design',
    capabilities: [
      'Credential technology (RFID, BLE, biometric) per door and per user role',
      'Door hardware selection (strike, maglock, mortise) per Fire / ADA / EN 1125 requirements',
      'Dual-control access at high-value rooms (vault, pharmacy, server suite)',
      'Visitor management with time-limited badges + auto-expiry',
      'Access schedules tied to shift patterns, holidays, lockdown events',
      'Audit logs retained 1-7 years depending on compliance regime',
    ],
  },
  'intrusion-detection': {
    slug: 'intrusion-detection',
    name: 'Intrusion Detection',
    shortName: 'intrusion detection design',
    about: 'Intrusion detection planning maps every entry point, occupied zone, and vulnerable surface to the right sensor: door / window contacts at the perimeter; PIR or dual-tech for interior motion; beam barriers along fence lines; glass-break sensors at large windows; vibration sensors on vaults / safes. Alarm zones group sensors so the panel can arm portions separately ("night" = perimeter only, "away" = perimeter + interior). Grade 2 vs Grade 3 (EN 50131) dictates redundancy, polling rates, and tamper requirements.',
    pillarUrl: '/intrusion-detection-design',
    capabilities: [
      'PIR placement covering interior choke points without overlap waste',
      'Door / window contacts on every perimeter opening',
      'Beam barriers across fence lines + driveway gates',
      'Glass-break detectors on shopfront / lobby glazing',
      'Vibration / shock sensors on vaults, safes, ATM enclosures',
      'Alarm zoning (perimeter / interior / 24h-armed) to support stay vs away modes',
      'Grade 2 vs Grade 3 (EN 50131) panel + polling redundancy choice',
    ],
  },
  'network-planning': {
    slug: 'network-planning',
    name: 'Network Planning',
    shortName: 'network planning for security',
    about: 'Network planning for security underpins everything: IP addressing for cameras + intrusion panels + access controllers, VLAN segmentation isolating CCTV / OT / customer networks, PoE budget calculations for the right switch tier, UPS sizing for the longest acceptable outage, cable routing through plenum + conduit + outdoor armoured runs, and bandwidth provisioning for 4K / 8MP camera streams with H.265 compression.',
    pillarUrl: '/network-planning-for-security',
    capabilities: [
      'IP addressing schema (subnetting) per camera / access / intrusion / monitoring zones',
      'VLAN segmentation isolating CCTV from OT / customer / public WiFi traffic',
      'PoE budget per switch tier (Type 1/2/3/4) and total PSE wattage headroom',
      'UPS sizing for 30 min / 1 h / 4 h / 24 h target backup time',
      'Cable type selection (Cat5e / Cat6 / Cat6a) per run length + PoE class + future-proofing',
      'Bandwidth provisioning for 4K H.265 streams + simultaneous remote viewers',
      'NVR storage tiering + retention windows + cold storage / cloud archive',
    ],
  },
  'security-compliance': {
    slug: 'security-compliance',
    name: 'Security Compliance',
    shortName: 'security compliance validation',
    about: 'Security compliance design validates every system component against the regulations and standards governing the property: GDPR (EU privacy), HIPAA (US healthcare), PCI-DSS (card data), SOX (financial reporting), NDAA Section 889 (US federal), EN 50131 (intrusion grade), EN 62676 (CCTV image quality), ISO 27001 (information security). Each framework has hard requirements on camera placement, retention period, access logging, equipment provenance, and audit trails.',
    pillarUrl: '/security-compliance',
    capabilities: [
      'Camera-placement audit (no cameras in private zones per privacy laws)',
      'Retention window validation (30-90 days / 1 year / 7 years per regulation)',
      'NDAA Section 889 equipment verification (no banned brands in federal projects)',
      'EN 62676-4 image-quality validation at every monitoring zone',
      'EN 50131 intrusion grade selection (Grade 2 commercial / Grade 3 banking)',
      'Audit-log retention + tamper-evidence per ISO 27001 / SOC 2',
      'Generated audit-ready compliance documentation (PDF, attached to project)',
    ],
  },
};

// ── Combine: produces all 36 (vertical, discipline) combinations ────────
export interface VerticalDisciplineEntry {
  slug: string;
  verticalKey: string;
  disciplineKey: DisciplineKey;
}

export function getAllVerticalDisciplineEntries(): VerticalDisciplineEntry[] {
  const out: VerticalDisciplineEntry[] = [];
  for (const v of Object.values(VERTICALS)) {
    for (const d of Object.values(DISCIPLINES)) {
      out.push({
        slug: `${v.slug}-${d.shortName.replace(/ /g, '-')}`.replace(/--+/g, '-'),
        verticalKey: v.slug,
        disciplineKey: d.slug,
      });
    }
  }
  return out;
}

/** All entries pre-built so route generation can reference them statically. */
export const VERTICAL_DISCIPLINE_ENTRIES: VerticalDisciplineEntry[] = getAllVerticalDisciplineEntries();
